/**
 * Palette `!` mode: run a tool directly. A small form is generated from the
 * tool's JSON Schema; approval-gated tools show the same approve/deny gate the
 * agent gets. Output renders as JSON with the call summary.
 */
import { useMemo, useState } from 'react';
import { Play, ShieldAlert, Check, X } from 'lucide-react';
import { Api, type InvokeResult, type JsonSchemaProp, type ToolInfo } from '../../lib/api';

export function ToolRunDialog({ tool, onClose }: { tool: ToolInfo; onClose: () => void }) {
  const props = useMemo(() => Object.entries(tool.schema?.properties ?? {}), [tool]);
  const required = useMemo(() => new Set(tool.schema?.required ?? []), [tool]);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const [k, p] of props) if (p.default !== undefined) v[k] = typeof p.default === 'string' ? p.default : JSON.stringify(p.default);
    return v;
  });
  const [result, setResult] = useState<InvokeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const build = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [k, p] of props) {
      const raw = values[k];
      if (raw === undefined || raw === '') continue;
      out[k] = coerce(raw, p);
    }
    return out;
  };

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      setResult(await Api.agent.invoke(tool.name, build()));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** The server holds the parked call and the input it was parked with; we only send the decision. */
  const decide = async (callId: string, approve: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      if (approve) setResult(await Api.agent.approveCall(callId));
      else {
        await Api.agent.denyCall(callId);
        setResult(null);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog tool-dialog">
        <div className="dialog-head">
          <span className="mono">{tool.name}</span>
          <span className={`chip ${tool.riskLevel === 'SAFE' ? 'ok' : tool.riskLevel === 'HIGH' ? 'crit' : tool.riskLevel === 'MEDIUM' ? 'warn' : ''}`}>{tool.riskLevel}</span>
          {tool.policy.requiresApproval && <span className="chip warn">approval</span>}
          <span className="spacer" />
          <button className="icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="dialog-body">
          <div className="muted" style={{ fontSize: 13 }}>{tool.description}</div>
          <div className="tool-form">
            {props.length === 0 && <div className="faint">No inputs.</div>}
            {props.map(([k, p]) => (
              <label key={k}>
                <span className="mono">{k}{required.has(k) ? ' *' : ''}</span>
                {p.enum ? (
                  <select value={values[k] ?? ''} onChange={(e) => setValues({ ...values, [k]: e.target.value })}>
                    <option value="">—</option>
                    {p.enum.map((o) => <option key={String(o)} value={String(o)}>{String(o)}</option>)}
                  </select>
                ) : typeOf(p) === 'boolean' ? (
                  <select value={values[k] ?? ''} onChange={(e) => setValues({ ...values, [k]: e.target.value })}>
                    <option value="">—</option><option value="true">true</option><option value="false">false</option>
                  </select>
                ) : (
                  <input type="text" value={values[k] ?? ''} placeholder={p.description ?? typeOf(p)} onChange={(e) => setValues({ ...values, [k]: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && void run()} />
                )}
                {p.description && <span className="faint" style={{ fontWeight: 400, fontSize: 11 }}>{p.description}</span>}
              </label>
            ))}
          </div>
          {err && <div className="pane-banner crit">{err}</div>}
          {result && (
            <div className="tool-result">
              {result.status === 'AWAITING_APPROVAL' ? (
                <div className="tool-approve">
                  <ShieldAlert size={14} />
                  <span>This tool needs approval. Run it now?</span>
                  <button className="primary" disabled={busy} onClick={() => void decide(result.record.id, true)}><Check size={12} /> Approve and run</button>
                  <button className="danger" disabled={busy} onClick={() => void decide(result.record.id, false)}><X size={12} /> Cancel</button>
                </div>
              ) : (
                <>
                  <div className="label">{result.status}{result.record.durationMs != null ? ` · ${result.record.durationMs} ms` : ''}</div>
                  {result.status === 'OK' ? (
                    <>
                      {result.record.summary && <div style={{ fontSize: 13, marginBottom: 6 }}>{result.record.summary}</div>}
                      <pre className="mono">{typeof result.output === 'string' ? result.output : JSON.stringify(result.output, null, 1)}</pre>
                    </>
                  ) : (
                    <div className="tool-err mono">{result.error}</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="dialog-foot">
          <button className="ghost" onClick={onClose}>Close</button>
          <button className="primary" onClick={() => void run()} disabled={busy}><Play size={12} /> Run</button>
        </div>
      </div>
    </div>
  );
}

function typeOf(p: JsonSchemaProp): string {
  if (Array.isArray(p.type)) return p.type[0];
  if (p.type) return p.type;
  if (p.anyOf?.length) return typeOf(p.anyOf[0]);
  return 'string';
}

function coerce(raw: string, p: JsonSchemaProp): unknown {
  const t = typeOf(p);
  if (t === 'number' || t === 'integer') {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (t === 'boolean') return raw === 'true';
  if (t === 'array' || t === 'object') {
    try {
      return JSON.parse(raw);
    } catch {
      return t === 'array' ? raw.split(',').map((s) => s.trim()).filter(Boolean) : raw;
    }
  }
  return raw;
}
